<script setup lang="ts">
import {ref, reactive} from "vue";
import {Dialog} from "../../base-components/Headless";
import Button from "../../base-components/Button";
import Lucide from "../../base-components/Lucide";
import {FormInput} from "../../base-components/Form";
import {userUserStore} from "../../stores/user";
import {EncuestaNPS, utilFechaGetCurrentTime} from "@fixeduy/fixedtypes";
import {actualizarEncuesta} from "../../services/cloudFunctions/actualizarEncuesta";
import {useMensajesStore} from "../../services/mensajes";
import LoadingIcon from "../../base-components/LoadingIcon";
import MobileDetect from "mobile-detect";

const userStore = userUserStore();
const openDialog = ref(true);
const pantallaNotaBaja = ref(false);
const pantallaNotaNeutra = ref(false);
const pantallaNotaAlta = ref(false);
const yaDejoResena = ref(false);
const noQuiereContestar = ref(false);
const placeID = "ChIJW1Kg_BqHn5URcVG_PMlXA0g";
const ulrResena = "https://search.google.com/local/writereview?placeid=";
const mensajesStore = useMensajesStore();
const autorUserId = userStore.userLoggedIn?.uid ?? "";
const autorNombre = userStore.userLoggedIn?.displayName ?? "";
const cargando = ref(false);
const md = new MobileDetect(window.navigator.userAgent);
const dispositivo = md.tablet() ? "Tablet" : md.mobile() ? "Celular" : "Computadora";
const timestampUnaSemana = 604800000;

interface Props {
  encuesta: EncuestaNPS;
}

const props = defineProps<Props>();

const formData = reactive<{
  voto: number | null;
  comentario: string;
  posponer: boolean;
}>({
  voto: null,
  comentario: "",
  posponer: false
});

const handleVoto = (calificacion: number) => {
  formData.voto = calificacion;

  if (calificacion < 7) {
    pantallaNotaBaja.value = true;
    pantallaNotaNeutra.value = false;
    pantallaNotaAlta.value = false;
  } else if (calificacion < 9) {
    pantallaNotaBaja.value = false;
    pantallaNotaNeutra.value = true;
    pantallaNotaAlta.value = false;
  } else {
    pantallaNotaBaja.value = false;
    pantallaNotaNeutra.value = false;
    pantallaNotaAlta.value = true;
  }
};

const posponerEncuesta = () => {
  console.log("Pospone encuesta");
  formData.posponer = true;
  finalizar();
};

const noContesta = () => {
  console.log("noContesta");
  noQuiereContestar.value = true;
  finalizar();
};

const yaDejoResenaFunc = () => {
  yaDejoResena.value = true;
  finalizar();
};

const finalizar = async () => {
  cargando.value = true;
  let tieneRespuesta = false;
  let voto = 0;
  let fechaGenerada = props.encuesta.fechaGenerada;
  if (!noQuiereContestar.value) {
    tieneRespuesta = true;
    voto = formData.voto ?? 0;
  }

  if (formData.posponer) {
    fechaGenerada = utilFechaGetCurrentTime() + timestampUnaSemana;
  }
  // Actualizar la encuesta con la nueva respuesta
  const encuestaActualizada: EncuestaNPS = {
    ...props.encuesta,
    pendiente: false,
    fechaRespuesta: utilFechaGetCurrentTime(),
    tieneRespuesta: tieneRespuesta,
    usuario: autorUserId,
    voto: voto,
    comentario: formData.comentario,
    respondeDesde: dispositivo,
    fechaGenerada: fechaGenerada,
    nombreEmpresa: userStore.empresa?.razonSocial,
    rut: userStore.empresa?.rut,
    nombreUsuario: autorNombre
  };
  console.log("Se envia>", encuestaActualizada);
  const resultado = await actualizarEncuesta(encuestaActualizada);
  if (resultado) {
    mensajesStore.mostrarToast("Encuesta finalizada con éxito", "", mensajesStore.mensajeType.exito);
  }

  if (yaDejoResena) {
    // Actualizar a nivel de usuario, que ya dejo resena
  }

  userStore.encuestaEnviada();
  openDialog.value = false;
  cargando.value = false;
};
</script>

<template>
  <Dialog :open="openDialog" @close="() => (openDialog = false)">
    <Dialog.Panel class="overflow-y-auto max-h-[80vh] p-6" style="width: 80vw; max-width: 800px">
      <Dialog.Description>
        <div class="text-center mb-6">
          <h4 class="text-2xl font-semibold mb-4">Encuesta de satisfacción</h4>
          <p class="m-2 text-lg">¿Qué posibilidades hay de que recomiendes Fixed a un amigo o colega?</p>

          <div class="flex flex-wrap sm:flex-nowrap justify-center mt-8 mb-8 items-center space-y-4 sm:space-y-0">
            <div v-if="dispositivo === 'Computadora'" class="hidden sm:block">
              <Lucide icon="Frown" class="mr-2 w-8 h-8 text-red-600" />
            </div>

            <div class="grid grid-cols-5 gap-4 sm:gap-0 sm:flex sm:space-x-4">
              <Button
                v-for="num in 10"
                :key="num"
                @click="handleVoto(num)"
                :class="[
                  'w-12 h-12 text-lg rounded-full flex items-center justify-center border',
                  formData.voto === num ? 'border-cyan-700 bg-cyan-100' : 'border-gray-300',
                  'focus:outline-none'
                ]"
                :autofocus="false"
              >
                {{ num }}
              </Button>
            </div>

            <div v-if="dispositivo === 'Computadora'" class="hidden sm:block">
              <Lucide icon="Smile" class="ml-2 w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>
        <div v-if="pantallaNotaBaja" class="text-center">
          <p class="mb-4 text-lg">¿Qué te tiene disconforme?</p>

          <FormInput
            type="text"
            v-model="formData.comentario"
            class="mb-4 p-4"
            placeholder="Comentarios opcionales..."
          />

          <div v-if="!cargando" class="flex justify-between">
            <Button class="bg-gray-500 text-white" @click="noContesta">No quiero responder</Button>
            <Button class="bg-blue-500 text-white" @click="finalizar">Enviar comentario</Button>
          </div>
          <div v-else class="w-6 mr-2 flex justify-center">
            <LoadingIcon icon="oval" class="w-6 h-6 mr-2" />
          </div>
        </div>

        <!-- Pantalla de voto neutro -->
        <div v-if="pantallaNotaNeutra" class="text-center">
          <p class="mb-4 text-lg">¿Tienes algún comentario para hacernos?</p>
          <FormInput
            type="text"
            v-model="formData.comentario"
            class="mb-4 p-4"
            placeholder="Comentarios opcionales..."
          />

          <div v-if="!cargando" class="flex justify-between">
            <Button class="bg-gray-500 text-white" @click="noContesta">No quiero responder</Button>
            <Button class="bg-blue-500 text-white" @click="finalizar">Enviar comentario</Button>
          </div>
          <div v-else class="w-6 mr-2 flex justify-center">
            <LoadingIcon icon="oval" class="w-6 h-6 mr-2" />
          </div>
        </div>

        <!-- Pantalla de voto alto -->
        <div v-if="pantallaNotaAlta" class="text-center">
          <p class="text-xl mb-4 font-semibold">¡Estamos contentos de que estés conforme!</p>
          <p class="mb-6 text-lg">Te agradecemos si puedes dejarnos una opinión en Google Maps</p>
          <p class="mb-4">
            <a :href="ulrResena + placeID" target="_blank" class="text-lg text-blue-600 underline">Dejar reseña</a>
          </p>
          <p class="mb-6 text-lg font-semibold">¡Gracias!</p>
          <div v-if="!cargando">
            <Button @click="yaDejoResenaFunc" variant="secondary" class="mr-4">Ya dejé reseña</Button>
            <Button @click="finalizar" class="bg-blue-500 text-white px-8 py-3">Cerrar y continuar al sistema</Button>
          </div>
          <div v-else class="w-6 mr-2 flex justify-center">
            <LoadingIcon icon="oval" class="w-6 h-6 mr-2" />
          </div>
        </div>

        <div v-if="!pantallaNotaBaja && !pantallaNotaNeutra && !pantallaNotaAlta" class="text-center">
          <div v-if="!cargando" class="flex justify-between">
            <div class="flex space-x-2">
              <Button variant="secondary" @click="noContesta">No quiero responder</Button>
              <Button variant="secondary" @click="posponerEncuesta" class="ml-4">Responder luego</Button>
            </div>
          </div>
          <div v-else class="w-6 mr-2 flex justify-center">
            <LoadingIcon icon="oval" class="w-6 h-6 mr-2" />
          </div>
        </div>
      </Dialog.Description>
    </Dialog.Panel>
  </Dialog>
</template>
<style>
button:focus {
  outline: none !important;
  box-shadow: none !important;
}
</style>
